import React, { useState } from 'react';
import { 
  Box, 
  Popover, 
  Typography,
  IconButton,
  ButtonGroup,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  alpha,
  Tooltip
} from '@mui/material';
import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatSize as FormatSizeIcon,
  LineWeight as LineWeightIcon,
  Height as HeightIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const EditableElement = ({ 
  children, 
  elementType = 'texto', 
  initialStyle = {}, 
  onStyleChange,
  customTheme,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [style, setStyle] = useState(initialStyle);
  
  // Estado para os diferentes tipos de formatação
  const [fontSize, setFontSize] = useState(style.fontSize || 'medium');
  const [fontStyles, setFontStyles] = useState(() => {
    const styles = [];
    if (style.fontWeight === 'bold') styles.push('bold');
    if (style.fontStyle === 'italic') styles.push('italic');
    if (style.textDecoration === 'underline') styles.push('underline');
    return styles;
  });
  const [spacing, setSpacing] = useState(style.margin || 'medium');
  const [lineHeight, setLineHeight] = useState(style.lineHeight || 'medium');

  const handleClick = (event) => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Manipuladores para alterações de estilo
  const handleFontSizeChange = (event, newSize) => {
    if (newSize !== null) {
      setFontSize(newSize);
      updateStyle({ fontSize: newSize });
    }
  };

  const handleFontStyleChange = (event, newStyles) => {
    setFontStyles(newStyles);
    
    const updatedStyle = {};
    updatedStyle.fontWeight = newStyles.includes('bold') ? 'bold' : 'normal';
    updatedStyle.fontStyle = newStyles.includes('italic') ? 'italic' : 'normal';
    updatedStyle.textDecoration = newStyles.includes('underline') ? 'underline' : 'none';
    
    updateStyle(updatedStyle);
  };

  const handleSpacingChange = (event, newSpacing) => {
    if (newSpacing !== null) {
      setSpacing(newSpacing);
      updateStyle({ margin: newSpacing });
    }
  };

  const handleLineHeightChange = (event, newLineHeight) => {
    if (newLineHeight !== null) {
      setLineHeight(newLineHeight);
      updateStyle({ lineHeight: newLineHeight });
    }
  };

  // Função auxiliar para atualizar estilos
  const updateStyle = (newStyleProps) => {
    const updatedStyle = { ...style, ...newStyleProps };
    setStyle(updatedStyle);
    if (onStyleChange) {
      onStyleChange(updatedStyle);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'style-popover' : undefined;

  // Indicador visual de elemento editável
  const renderEditableIndicator = () => (
    <Tooltip title="Clique para editar estilo">
      <Box 
        sx={{ 
          position: 'absolute',
          top: -10,
          right: -10,
          zIndex: 1,
          opacity: 0,
          transition: 'opacity 0.2s',
          '.editable-element:hover &': {
            opacity: 0.8
          }
        }}
      >
        <IconButton 
          size="small" 
          sx={{ 
            backgroundColor: alpha(customTheme.palette.primary.main, 0.1),
            '&:hover': { 
              backgroundColor: alpha(customTheme.palette.primary.main, 0.2)
            },
            boxShadow: 1,
            padding: '2px'
          }}
        >
          <SettingsIcon fontSize="inherit" sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      </Box>
    </Tooltip>
  );

  return (
    <Box 
      className="editable-element"
      onClick={handleClick}
      sx={{ 
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.2s',
        '&:hover': !disabled && {
          boxShadow: `0 0 0 1px ${alpha(customTheme.palette.primary.main, 0.4)}`,
          backgroundColor: alpha(customTheme.palette.primary.main, 0.03),
          borderRadius: '2px'
        }
      }}
    >
      {!disabled && renderEditableIndicator()}
      {children}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted={false}
        container={document.body}
        disablePortal={false}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: 2,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            mt: 1,
            p: 2,
            width: 300
          }
        }}
      >
        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          Personalizar {elementType}
        </Typography>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Tamanho da fonte */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block" gutterBottom sx={{ color: 'text.secondary' }}>
            Tamanho do texto:
          </Typography>
          <ToggleButtonGroup
            value={fontSize}
            exclusive
            onChange={handleFontSizeChange}
            aria-label="tamanho de fonte"
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="small" aria-label="pequeno" sx={{ flex: 1 }}>
              <FormatSizeIcon fontSize="small" sx={{ transform: 'scale(0.8)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Pequeno</Typography>
            </ToggleButton>
            <ToggleButton value="medium" aria-label="médio" sx={{ flex: 1 }}>
              <FormatSizeIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Médio</Typography>
            </ToggleButton>
            <ToggleButton value="large" aria-label="grande" sx={{ flex: 1 }}>
              <FormatSizeIcon fontSize="small" sx={{ transform: 'scale(1.2)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Grande</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Estilo da fonte */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block" gutterBottom sx={{ color: 'text.secondary' }}>
            Estilo do texto:
          </Typography>
          <ToggleButtonGroup
            value={fontStyles}
            onChange={handleFontStyleChange}
            aria-label="estilo de texto"
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="bold" aria-label="negrito" sx={{ flex: 1 }}>
              <FormatBoldIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Negrito</Typography>
            </ToggleButton>
            <ToggleButton value="italic" aria-label="itálico" sx={{ flex: 1 }}>
              <FormatItalicIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Itálico</Typography>
            </ToggleButton>
            <ToggleButton value="underline" aria-label="sublinhado" sx={{ flex: 1 }}>
              <FormatUnderlinedIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Sublinhado</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Espaçamento inferior */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block" gutterBottom sx={{ color: 'text.secondary' }}>
            Espaçamento inferior:
          </Typography>
          <ToggleButtonGroup
            value={spacing}
            exclusive
            onChange={handleSpacingChange}
            aria-label="espaçamento"
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="small" aria-label="pequeno" sx={{ flex: 1 }}>
              <LineWeightIcon fontSize="small" sx={{ transform: 'scale(0.8)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Pequeno</Typography>
            </ToggleButton>
            <ToggleButton value="medium" aria-label="médio" sx={{ flex: 1 }}>
              <LineWeightIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Médio</Typography>
            </ToggleButton>
            <ToggleButton value="large" aria-label="grande" sx={{ flex: 1 }}>
              <LineWeightIcon fontSize="small" sx={{ transform: 'scale(1.2)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Grande</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Altura da linha */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block" gutterBottom sx={{ color: 'text.secondary' }}>
            Altura da linha:
          </Typography>
          <ToggleButtonGroup
            value={lineHeight}
            exclusive
            onChange={handleLineHeightChange}
            aria-label="altura da linha"
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="small" aria-label="pequeno" sx={{ flex: 1 }}>
              <HeightIcon fontSize="small" sx={{ transform: 'scale(0.8)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Pequeno</Typography>
            </ToggleButton>
            <ToggleButton value="medium" aria-label="médio" sx={{ flex: 1 }}>
              <HeightIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Médio</Typography>
            </ToggleButton>
            <ToggleButton value="large" aria-label="grande" sx={{ flex: 1 }}>
              <HeightIcon fontSize="small" sx={{ transform: 'scale(1.2)' }} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Grande</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Popover>
    </Box>
  );
};

export default EditableElement; 